import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useSize } from '../hooks/useSize';
import {
    addMessengerGroupMembers,
    createMessengerGroupChat,
    fetchMessengerAttachmentData,
    loadMessengerChats,
    loadMessengerGroupMembers,
    loadMessengerRecipients,
    loadMessengerThread,
    markMessengerChatRead,
    openFlightChat,
    openMessengerAttachment,
    sendMessengerMessage,
    setMessengerActiveChat,
    setMessengerFlightScope,
    setMessengerSearch,
    startPersonalChat,
} from '../../actions/messenger';
import {
    useMessengerActiveChat,
    useMessengerChats,
    useMessengerFlightScoped,
    useMessengerFlightScopePlanFlightId,
    useMessengerRecipients,
    useMessengerSearch,
    useMessengerThread,
} from '../../selectors/messenger';
import { getCurrentFlight } from '../../selectors/screen';

import { useVisualViewportBottomInset } from './useVisualViewportBottomInset';

const NARROW_BREAKPOINT = 760;
// Период обновления открытой ветки: входящие и галочки прочтения появляются
// без переоткрытия чата. 5 c — «живой» диалог при приемлемой нагрузке на IS.
const ACTIVE_THREAD_POLL_MS = 5000;

export const useMessengerPage = () => {
    const dispatch = useDispatch();
    const { width } = useSize();
    const isNarrow = width < NARROW_BREAKPOINT;
    const bottomInset = useVisualViewportBottomInset(isNarrow);

    const chats = useMessengerChats();
    const search = useMessengerSearch();
    const activeChat = useMessengerActiveChat();
    const messages = useMessengerThread(activeChat);
    const recipients = useMessengerRecipients();
    const isFlightScoped = useMessengerFlightScoped();
    const flightScopePlanFlightId = useMessengerFlightScopePlanFlightId();
    const currentFlightId = useSelector((state) => getCurrentFlight(state)?.id);

    const [composeOpen, setComposeOpen] = useState(false);
    // Открытое фото в полноэкранном предпросмотре: { src, name } либо null.
    const [imagePreview, setImagePreview] = useState(null);
    // Диалог состава группы: { chat } либо null + текущий список участников.
    const [membersChat, setMembersChat] = useState(null);
    const [groupMembers, setGroupMembers] = useState([]);

    useEffect(() => {
        if (isFlightScoped) {
            if (!activeChat || activeChat.planFlightId !== flightScopePlanFlightId) {
                dispatch(openFlightChat(flightScopePlanFlightId));
            }

            return;
        }

        if (flightScopePlanFlightId != null && currentFlightId == null) {
            dispatch(setMessengerFlightScope(null));
        }

        dispatch(loadMessengerChats());
    }, [dispatch, isFlightScoped, flightScopePlanFlightId, currentFlightId, activeChat, activeChat?.planFlightId]);

    useEffect(() => {
        if (!isFlightScoped || !currentFlightId || currentFlightId === flightScopePlanFlightId) {
            return;
        }

        dispatch(openFlightChat(currentFlightId));
    }, [dispatch, isFlightScoped, currentFlightId, flightScopePlanFlightId]);

    // Живое обновление открытой ветки: периодически подтягиваем новые сообщения
    // активного чата (и отмечаем входящие прочитанными на сервере). Дополнительно
    // обновляем сразу при возврате фокуса/видимости вкладки — чтобы не ждать тик
    // таймера после переключения. Без сети/чата — no-op (проверки внутри thunk'а).
    useEffect(() => {
        if (!activeChat) {
            return undefined;
        }

        const refresh = () => dispatch(loadMessengerThread(activeChat));

        // Сразу подтянуть актуальное состояние при открытии/смене чата.
        refresh();

        const timer = setInterval(refresh, ACTIVE_THREAD_POLL_MS);

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                refresh();
            }
        };

        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [dispatch, activeChat]);

    const handleSearch = useCallback((value) => dispatch(setMessengerSearch(value)), [dispatch]);

    const handleSelect = useCallback(
        (chat) => {
            dispatch(setMessengerActiveChat(chat));
            dispatch(loadMessengerThread(chat));
            dispatch(markMessengerChatRead(chat));
        },
        [dispatch]
    );

    const handleBack = useCallback(() => dispatch(setMessengerActiveChat(null)), [dispatch]);

    const handleSend = useCallback(
        (text, attachments) => {
            if (activeChat) {
                dispatch(sendMessengerMessage(activeChat, text, attachments));
            }
        },
        [dispatch, activeChat]
    );

    const handleOpenAttachment = useCallback((attachment) => dispatch(openMessengerAttachment(attachment)), [dispatch]);

    // Ленивая подгрузка содержимого вложения (для миниатюр фото в ленте).
    const getAttachmentData = useCallback((attachment) => dispatch(fetchMessengerAttachmentData(attachment)), [dispatch]);

    const handleOpenImagePreview = useCallback((src, name) => setImagePreview({ src, name }), []);

    const handleCloseImagePreview = useCallback(() => setImagePreview(null), []);

    const handleOpenCompose = useCallback(() => {
        dispatch(loadMessengerRecipients());
        setComposeOpen(true);
    }, [dispatch]);

    const handleCloseCompose = useCallback(() => setComposeOpen(false), []);

    const handleSelectRecipient = useCallback(
        (recipient) => {
            setComposeOpen(false);
            dispatch(startPersonalChat(recipient));
        },
        [dispatch]
    );

    const handleCreateGroup = useCallback(
        (name, memberIds) => {
            if (!name || !name.trim()) {
                return;
            }

            setComposeOpen(false);
            dispatch(createMessengerGroupChat(name, memberIds));
        },
        [dispatch]
    );

    const handleOpenMembers = useCallback(
        (chat) => {
            setMembersChat(chat);
            setGroupMembers([]);
            dispatch(loadMessengerRecipients());
            dispatch(loadMessengerGroupMembers(chat)).then((list) => setGroupMembers(list || []));
        },
        [dispatch]
    );

    const handleCloseMembers = useCallback(() => setMembersChat(null), []);

    const handleAddMembers = useCallback(
        (memberIds) => {
            if (!membersChat || !memberIds || !memberIds.length) {
                return;
            }

            dispatch(addMessengerGroupMembers(membersChat, memberIds)).then((list) => {
                if (list) {
                    setGroupMembers(list);
                }
            });
        },
        [dispatch, membersChat]
    );

    return {
        isNarrow,
        bottomInset,
        isFlightScoped,
        chats,
        search,
        activeChat,
        messages,
        recipients,
        composeOpen,
        imagePreview,
        membersChat,
        groupMembers,
        handleSearch,
        handleSelect,
        handleBack,
        handleSend,
        handleOpenAttachment,
        getAttachmentData,
        handleOpenImagePreview,
        handleCloseImagePreview,
        handleOpenCompose,
        handleCloseCompose,
        handleSelectRecipient,
        handleCreateGroup,
        handleOpenMembers,
        handleCloseMembers,
        handleAddMembers,
    };
};
